import { MatchOperator, PatternHelp } from "./MatchOperator";
import { Plus, Trash2, X } from "lucide-react";
import { uid, type Project } from "@proof/core";

type Rule = Project["triggers"][number];
const actions = [
  { key: "show", label: "Show" },
  { key: "hide", label: "Hide" },
  { key: "clear", label: "Hide and clear" },
] as const;

/** Rules read as sentences: when a field has a value, show or hide things. */
export function RuleBuilder({
  project: p,
  onChange,
}: {
  project: Project;
  onChange: (project: Project) => void;
}) {
  const nameOf = (id: string) =>
    p.fields.find((f) => f.id === id)?.label ||
    (p.groups.find((g) => g.id === id)?.name ?? "Unknown") + " (section)";
  const update = (id: string, patch: Partial<Rule>) =>
    onChange({
      ...p,
      triggers: p.triggers.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    });
  const sources = p.fields.filter((f) => f.key);
  return (
    <div className="rule-builder">
      {!p.triggers.length && (
        <p className="muted rule-empty">
          No rules yet. Rules let one answer change the form, for example
          showing a “Gift message” field only when “Is this a gift?” is checked.
        </p>
      )}
      {p.triggers.map((rule, index) => {
        const source = p.fields.find((f) => f.id === rule.sourceId);
        const excluded = Object.values(rule.exclude).reduce(
          (n, list) => n + list.length,
          0,
        );
        return (
          <section
            key={rule.id}
            className="rule-card"
            aria-label={`Rule ${index + 1}`}
          >
            <div className="rule-line">
              <span className="rule-word">When</span>
              <select
                aria-label={`Rule ${index + 1} field`}
                value={rule.sourceId}
                onChange={(e) =>
                  update(rule.id, { sourceId: e.target.value, value: "" })
                }
              >
                {!source && (
                  <option value={rule.sourceId}>Choose a field</option>
                )}
                {sources.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.label}
                  </option>
                ))}
              </select>
              <MatchOperator
                label={`Rule ${index + 1} operator`}
                value={rule.op}
                onChange={(op) => update(rule.id, { op })}
              />
              {(!rule.op || rule.op === "eq") && source?.type === "checkbox" ? (
                <select
                  aria-label={`Rule ${index + 1} value`}
                  value={String(rule.value === true)}
                  onChange={(e) =>
                    update(rule.id, { value: e.target.value === "true" })
                  }
                >
                  <option value="true">checked</option>
                  <option value="false">not checked</option>
                </select>
              ) : (!rule.op || rule.op === "eq") && source?.choices.length ? (
                <select
                  aria-label={`Rule ${index + 1} value`}
                  value={String(rule.value ?? "")}
                  onChange={(e) => update(rule.id, { value: e.target.value })}
                >
                  <option value="">(empty)</option>
                  {source.choices.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  aria-label={`Rule ${index + 1} value`}
                  placeholder="a value"
                  value={String(rule.value ?? "")}
                  onChange={(e) => update(rule.id, { value: e.target.value })}
                />
              )}
              <button
                type="button"
                className="icon-button danger rule-delete"
                aria-label={`Delete rule ${index + 1}`}
                onClick={() =>
                  onChange({
                    ...p,
                    triggers: p.triggers.filter((r) => r.id !== rule.id),
                  })
                }
              >
                <Trash2 size={15} />
              </button>
            </div>
            <PatternHelp op={rule.op} value={rule.value} />
            {actions.map(({ key, label }) => (
              <div className="rule-line rule-targets" key={key}>
                <span className="rule-word">{label}</span>
                {rule[key].map((id) => (
                  <span className="chip" key={id}>
                    {nameOf(id)}
                    <button
                      type="button"
                      aria-label={`${label}: remove ${nameOf(id)}`}
                      onClick={() =>
                        update(rule.id, {
                          [key]: rule[key].filter((x) => x !== id),
                        })
                      }
                    >
                      <X size={11} />
                    </button>
                  </span>
                ))}
                <select
                  aria-label={`Rule ${index + 1} ${label.toLowerCase()}`}
                  value=""
                  onChange={(e) =>
                    e.target.value &&
                    update(rule.id, { [key]: [...rule[key], e.target.value] })
                  }
                >
                  <option value="">+ Add…</option>
                  {p.groups.length > 0 && (
                    <optgroup label="Sections">
                      {p.groups
                        .filter((g) => !rule[key].includes(g.id))
                        .map((g) => (
                          <option key={g.id} value={g.id}>
                            {g.name}
                          </option>
                        ))}
                    </optgroup>
                  )}
                  <optgroup label="Fields">
                    {p.fields
                      .filter(
                        (f) =>
                          f.id !== rule.sourceId && !rule[key].includes(f.id),
                      )
                      .map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.label}
                        </option>
                      ))}
                  </optgroup>
                </select>
              </div>
            ))}
            {excluded > 0 && (
              <p className="muted rule-note">
                Also removes {excluded} dropdown option
                {excluded === 1 ? "" : "s"}. Edit those in Advanced setup.
              </p>
            )}
          </section>
        );
      })}
      <button
        type="button"
        className="button"
        disabled={!sources.length}
        onClick={() =>
          onChange({
            ...p,
            triggers: [
              ...p.triggers,
              {
                id: uid("trigger"),
                sourceId: sources[0]?.id || "",
                value: sources[0]?.type === "checkbox" ? true : "",
                show: [],
                hide: [],
                clear: [],
                exclude: {},
              },
            ],
          })
        }
      >
        <Plus size={15} />
        Add rule
      </button>
    </div>
  );
}
